import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { HelmetProvider } from "react-helmet-async";

// Public Pages
import HomePage from "@/pages/public/HomePage";
import AboutPage from "@/pages/public/AboutPage";
import ServicesPage from "@/pages/public/ServicesPage";
import BecomeDealer from "@/pages/public/BecomeDealer";
import ContactPage from "@/pages/public/ContactPage";
import LocationsPage from "@/pages/public/LocationsPage";
import LocationsByStatePage from "@/pages/public/LocationsByStatePage";
import LocationDetailPage from "@/pages/public/LocationDetailPage";
import LocationsByCityPage from "@/pages/public/LocationsByCityPage";
import BlogPage from "@/pages/public/BlogPage";
import BlogPostPage from "@/pages/public/BlogPostPage";
import GalleryPage from "@/pages/public/GalleryPage";
import OurProcessPage from "@/pages/public/OurProcessPage";
import OpenStoreListPage from "@/pages/public/OpenStoreListPage";
import HowToOpenStorePage from "@/pages/public/HowToOpenStorePage";
import DealerStoreContent from "@/pages/dealer/DealerStoreContent";
import UpcomingStoresPage from "@/pages/public/UpcomingStoresPage";
import ReadyToGoStoresPage from "@/pages/public/ReadyToGoStoresPage";
import OpenStorePage from "@/pages/public/OpenStorePage";
import CityPage from "@/pages/public/CityPage";
import FaqPage from "@/pages/public/FaqPage";
import CareersPage from "@/pages/public/CareersPage";
import CareerDetailPage from "@/pages/public/CareerDetailPage";
import SitemapPage from "@/pages/public/SitemapPage";
import TotalWirelessMasterAgentPage from "@/pages/public/TotalWirelessMasterAgentPage";
import KeywordLandingPage from "@/pages/public/KeywordLandingPage";
import { KEYWORD_SLUGS } from "@/data/keywordLandingConfig";

// Auth Pages
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";

// Dealer Portal Pages
import DealerDashboard from "@/pages/dealer/DealerDashboard";
import DealerKnowledgeBase from "@/pages/dealer/DealerKnowledgeBase";
import DealerAnnouncements from "@/pages/dealer/DealerAnnouncements";
import DealerSubmitLocation from "@/pages/dealer/DealerSubmitLocation";
import DealerTraining from "@/pages/dealer/DealerTraining";
import DealerDocuments from "@/pages/dealer/DealerDocuments";
import BusinessPlanOrgChartBuilder from "@/pages/dealer/BusinessPlanOrgChartBuilder";
import PreApprovedDocuments from "@/pages/dealer/PreApprovedDocuments";
import W9Generator from "@/pages/dealer/W9Generator";
import MTCFormGenerator from "@/pages/dealer/MTCFormGenerator";
import MerchantAgreementGenerator from "@/pages/dealer/MerchantAgreementGenerator";
import DoorApplicationGenerator from "@/pages/dealer/DoorApplicationGenerator";
import RetailerAgreementGenerator from "@/pages/dealer/RetailerAgreementGenerator";
import DealerMarketplace from "@/pages/dealer/DealerMarketplace";
import DealerVisits from "@/pages/dealer/DealerVisits";
import DealerLocations from "@/pages/dealer/DealerLocations";
import DealerEmployees from "@/pages/dealer/DealerEmployees";
import DealerChat from "@/pages/dealer/DealerChat";
import DealerApplicants from "@/pages/dealer/DealerApplicants";

// Admin Portal Pages
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminDealers from "@/pages/admin/AdminDealers";
import AdminApplications from "@/pages/admin/AdminApplications";
import AdminAnnouncements from "@/pages/admin/AdminAnnouncements";
import AdminLocations from "@/pages/admin/AdminLocations";
import AdminCities from "@/pages/admin/AdminCities";
import AdminPages from "@/pages/admin/AdminPages";
import AdminSEO from "@/pages/admin/AdminSEO";
import AdminBlog from "@/pages/admin/AdminBlog";
import AdminGallery from "@/pages/admin/AdminGallery";
import AdminResumes from "@/pages/admin/AdminResumes";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminInventory from "@/pages/admin/AdminInventory";
import AdminMarketplaceOrders from "@/pages/admin/AdminMarketplaceOrders";
import AdminKnowledgeBase from "@/pages/admin/AdminKnowledgeBase";
import AdminTraining from "@/pages/admin/AdminTraining";
import AdminDealerVisits from "@/pages/admin/AdminDealerVisits";
import AdminChat from "@/pages/admin/AdminChat";
import AdminDeals from "@/pages/admin/AdminDeals";
import AdminVerizonSales from "@/pages/admin/AdminVerizonSales";
import DealerVerizonSales from "@/pages/dealer/DealerVerizonSales";
import AdminReports from "@/pages/admin/AdminReports";
import AdminAuditLogReport from "@/pages/admin/AdminAuditLogReport";
import AdminInventoryRemovalsReport from "@/pages/admin/AdminInventoryRemovalsReport";
import AdminSentEmailsReport from "@/pages/admin/AdminSentEmailsReport";
import AdminReportHistory from "@/pages/admin/AdminReportHistory";
import { PageviewTracker } from "@/components/shared/PageviewTracker";

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <>
      <PageviewTracker />
      <Routes>
      {/* Public Routes */}
      <Route path="/" element={<HomePage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/become-dealer" element={<BecomeDealer />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/locations" element={<LocationsPage />} />
      <Route path="/locations/:state" element={<LocationsByStatePage />} />
      <Route path="/locations/:state/:city" element={<LocationsByCityPage />} />
      <Route path="/locations/:state/:city/:address" element={<LocationDetailPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      <Route path="/faqs" element={<FaqPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/careers/:jobSlug" element={<CareerDetailPage />} />
      <Route path="/our-process" element={<OurProcessPage />} />
      <Route path="/open-a-store" element={<OpenStoreListPage />} />
      <Route path="/how-to-open-a-total-wireless-store" element={<HowToOpenStorePage />} />
      <Route path="/upcoming-stores" element={<UpcomingStoresPage />} />
      <Route path="/ready-to-go-stores" element={<ReadyToGoStoresPage />} />
      <Route path="/sitemap" element={<SitemapPage />} />
      <Route path="/totalwirelessmasteragent" element={<TotalWirelessMasterAgentPage />} />

      {/* SEO URL aliases — 301-equivalent redirects to canonical pages (capture direct-match searches) */}
      <Route path="/become-total-by-verizon-dealer" element={<Navigate to="/become-dealer" replace />} />
      <Route path="/become-total-wireless-dealer" element={<Navigate to="/become-dealer" replace />} />
      <Route path="/become-total-wireless-retailer" element={<Navigate to="/become-dealer" replace />} />
      <Route path="/total-by-verizon-dealer" element={<Navigate to="/become-dealer" replace />} />
      <Route path="/total-wireless-dealer-sign-up" element={<Navigate to="/become-dealer" replace />} />
      <Route path="/total-wireless-franchise" element={<Navigate to="/become-dealer" replace />} />
      <Route path="/total-by-verizon-franchise" element={<Navigate to="/become-dealer" replace />} />
      <Route path="/total-by-verizon-master-agent" element={<Navigate to="/totalwirelessmasteragent" replace />} />
      <Route path="/total-wireless-master-agent" element={<Navigate to="/totalwirelessmasteragent" replace />} />
      <Route path="/open-total-by-verizon-store" element={<Navigate to="/open-a-store" replace />} />

      {/* Keyword landing pages — must come BEFORE /:stateSlug catch-all.
          Each kind has a national variant and a per-state variant. */}
      {KEYWORD_SLUGS.flatMap((slug) => [
        <Route key={slug} path={`/${slug}`} element={<KeywordLandingPage kind={slug} />} />,
        <Route key={`${slug}-state`} path={`/${slug}/:stateSlug`} element={<KeywordLandingPage kind={slug} />} />,
      ])}

      {/* Dedicated city pages — must come BEFORE /:stateSlug catch-all */}
      <Route path="/cities/:citySlug" element={<CityPage />} />

      <Route path="/:stateSlug" element={<OpenStorePage />} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      
      {/* Dealer Portal Routes */}
      <Route path="/dealer" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dealer/dashboard" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/dealer/knowledge-base" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerKnowledgeBase />
        </ProtectedRoute>
      } />
      <Route path="/dealer/announcements" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerAnnouncements />
        </ProtectedRoute>
      } />
      <Route path="/dealer/orders" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerSubmitLocation />
        </ProtectedRoute>
      } />
      <Route path="/dealer/submit-location" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerSubmitLocation />
        </ProtectedRoute>
      } />
      <Route path="/dealer/training" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerTraining />
        </ProtectedRoute>
      } />
      <Route path="/dealer/documents" element={
        <ProtectedRoute allowedRoles={["dealer", "admin", "applicant"]}>
          <DealerDocuments />
        </ProtectedRoute>
      } />
      <Route path="/dealer/business-plan-org-chart" element={
        <ProtectedRoute allowedRoles={["dealer", "admin", "applicant"]}>
          <BusinessPlanOrgChartBuilder />
        </ProtectedRoute>
      } />
      <Route path="/dealer/org-chart" element={
        <ProtectedRoute allowedRoles={["dealer", "admin", "applicant"]}>
          <Navigate to="/dealer/business-plan-org-chart" replace />
        </ProtectedRoute>
      } />
      <Route path="/dealer/business-plan" element={
        <ProtectedRoute allowedRoles={["dealer", "admin", "applicant"]}>
          <Navigate to="/dealer/business-plan-org-chart" replace />
        </ProtectedRoute>
      } />
      <Route path="/dealer/pre-approved-documents" element={
        <ProtectedRoute allowedRoles={["dealer", "admin", "applicant"]}>
          <Navigate to="/dealer/documents" replace />
        </ProtectedRoute>
      } />
      <Route path="/dealer/w9-generator" element={
        <ProtectedRoute allowedRoles={["dealer", "admin", "applicant"]}>
          <W9Generator />
        </ProtectedRoute>
      } />
      <Route path="/dealer/mtc-form-generator" element={
        <ProtectedRoute allowedRoles={["dealer", "admin", "applicant"]}>
          <MTCFormGenerator />
        </ProtectedRoute>
      } />
      <Route path="/dealer/merchant-agreement-generator" element={
        <ProtectedRoute allowedRoles={["dealer", "admin", "applicant"]}>
          <MerchantAgreementGenerator />
        </ProtectedRoute>
      } />
      <Route path="/dealer/door-application-generator" element={
        <ProtectedRoute allowedRoles={["dealer", "admin", "applicant"]}>
          <DoorApplicationGenerator />
        </ProtectedRoute>
      } />
      <Route path="/dealer/retailer-agreement-generator" element={
        <ProtectedRoute allowedRoles={["dealer", "admin", "applicant"]}>
          <RetailerAgreementGenerator />
        </ProtectedRoute>
      } />
      <Route path="/dealer/marketplace" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerMarketplace />
        </ProtectedRoute>
      } />
      <Route path="/dealer/visits" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerVisits />
        </ProtectedRoute>
      } />
      <Route path="/dealer/store-content" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerStoreContent />
        </ProtectedRoute>
      } />
      <Route path="/dealer/locations" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerLocations />
        </ProtectedRoute>
      } />
      <Route path="/dealer/verizon-sales" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerVerizonSales />
        </ProtectedRoute>
      } />
      <Route path="/dealer/employees" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerEmployees />
        </ProtectedRoute>
      } />
      <Route path="/dealer/chat" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerChat />
        </ProtectedRoute>
      } />
      <Route path="/dealer/applicants" element={
        <ProtectedRoute allowedRoles={["dealer", "admin"]}>
          <DealerApplicants />
        </ProtectedRoute>
      } />
      
      {/* Admin Portal Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/leads" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminLeads />
        </ProtectedRoute>
      } />
      <Route path="/admin/dealers" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminDealers />
        </ProtectedRoute>
      } />
      <Route path="/admin/applications" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminApplications />
        </ProtectedRoute>
      } />
      <Route path="/admin/announcements" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminAnnouncements />
        </ProtectedRoute>
      } />
      <Route path="/admin/locations" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminLocations />
        </ProtectedRoute>
      } />
      <Route path="/admin/cities" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminCities />
        </ProtectedRoute>
      } />
      <Route path="/admin/settings" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminSettings />
        </ProtectedRoute>
      } />
      <Route path="/admin/pages" element={<Navigate to="/admin/settings" replace />} />
      <Route path="/admin/seo" element={<Navigate to="/admin/settings" replace />} />
      <Route path="/admin/users" element={<Navigate to="/admin/settings" replace />} />
      <Route path="/admin/blog" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminBlog />
        </ProtectedRoute>
      } />
      <Route path="/admin/state-images" element={<Navigate to="/admin/blog" replace />} />
      <Route path="/admin/gallery" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminGallery />
        </ProtectedRoute>} />
      <Route path="/admin/resumes" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminResumes />
        </ProtectedRoute>
      } />
      <Route path="/admin/inventory" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminInventory />
        </ProtectedRoute>
      } />
      <Route path="/admin/marketplace-orders" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminMarketplaceOrders />
        </ProtectedRoute>
      } />
      <Route path="/admin/knowledge-base" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminKnowledgeBase />
        </ProtectedRoute>
      } />
      <Route path="/admin/training" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminTraining />
        </ProtectedRoute>
      } />
      <Route path="/admin/dealer-visits" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminDealerVisits />
        </ProtectedRoute>
      } />
      <Route path="/admin/chat" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminChat />
        </ProtectedRoute>
      } />
      <Route path="/admin/verizon-sales" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminVerizonSales />
        </ProtectedRoute>
      } />
      <Route path="/admin/deals" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminDeals />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminReports />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports/audit-log" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminAuditLogReport />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports/dealer-visits" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminDealerVisits embedded />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports/resumes" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminResumes embedded />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports/inventory-removals" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminInventoryRemovalsReport />
        </ProtectedRoute>
      } />
      <Route path="/admin/reports/sent-emails" element={
        <ProtectedRoute allowedRoles={["admin", "staff"]}>
          <AdminSentEmailsReport />
        </ProtectedRoute>
      } />
      {["dlar", "pu40", "pulse", "avb", "leaderboard"].map((k) => (
        <Route key={k} path={`/admin/reports/history-${k}`} element={
          <ProtectedRoute allowedRoles={["admin", "staff"]}>
            <AdminReportHistory report={k} />
          </ProtectedRoute>
        } />
      ))}
      <Route path="/admin/reports/dealer-sales-history" element={<Navigate to="/admin/reports/history-dlar" replace />} />
      {/* Legacy direct URL still works (keeps deep-link bookmarks alive) */}
      <Route path="/admin/analytics" element={<Navigate to="/admin" replace />} />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
